/**
 * Store global - Gestion d'état minimale avec pub/sub
 */

const listeners = new Map();
let state = {
  user: null,
  isAuthenticated: false,
  
  // Multi-tenant WebEdena
  currentEtablissement: null, // { id, nom, ... }
  currentSite: null, // { id, nom, fuseau_horaire, ... }
  userRole: null, // rôle réel issu de l'auth
  simulatedUserRole: null, // rôle simulé réservé au super_admin
  mesEtablissements: [], // Liste des établissements accessibles
  
  uiStaffLabel: null,
  uiStaffLabelSiteId: null,
  
  // Réservation (renommés pour correspondre à WebEdena)
  currentPrestation: null, // Au lieu de currentService
  currentPrestations: null,
  currentEmploye: null, // Au lieu de currentStaff
  bookingDraft: null,
};

function getEffectiveUserRole(sourceState = state) {
  const actualRole = sourceState.userRole ?? null;
  const simulatedRole = sourceState.simulatedUserRole ?? null;
  return actualRole === 'super_admin' && simulatedRole ? simulatedRole : actualRole;
}

function getPublicState(sourceState = state) {
  return {
    ...sourceState,
    actualUserRole: sourceState.userRole ?? null,
    userRole: getEffectiveUserRole(sourceState),
  };
}

/**
 * Charge l'état depuis le localStorage
 */
function loadState() {
  try {
    const saved = localStorage.getItem('edena-state');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Restaurer uniquement certaines clés
      if (parsed.user) state.user = parsed.user;
      if (parsed.isAuthenticated) state.isAuthenticated = parsed.isAuthenticated;
    }
  } catch (error) {
    console.error('Erreur lors du chargement de l\'état:', error);
  }
}

/**
 * Sauvegarde l'état dans le localStorage (clés sélectives)
 */
function saveState() {
  try {
    const toSave = {
      user: state.user,
      isAuthenticated: state.isAuthenticated,
    };
    localStorage.setItem('edena-state', JSON.stringify(toSave));
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de l\'état:', error);
  }
}

/**
 * Récupère l'état complet ou une propriété spécifique
 * @param {string} [key] - Clé optionnelle
 * @returns {any}
 */
export function getState(key) {
  if (!key) return getPublicState();
  if (key === 'userRole') return getEffectiveUserRole();
  if (key === 'actualUserRole') return state.userRole ?? null;
  return state[key];
}

/**
 * Met à jour l'état et notifie les abonnés
 * @param {object} updates - Objet avec les mises à jour
 */
export function setState(updates) {
  const prevState = { ...state };
  const prevPublicState = getPublicState(prevState);
  const nextActualUserRole = Object.prototype.hasOwnProperty.call(updates, 'userRole')
    ? updates.userRole
    : prevState.userRole;

  state = {
    ...state,
    ...updates,
    simulatedUserRole: nextActualUserRole === 'super_admin'
      ? (Object.prototype.hasOwnProperty.call(updates, 'simulatedUserRole') ? updates.simulatedUserRole : state.simulatedUserRole)
      : null,
  };
  const nextPublicState = getPublicState(state);

  // Persister certaines clés
  if (updates.user !== undefined || updates.isAuthenticated !== undefined) {
    saveState();
  }

  // Notifier les abonnés
  const directKeysToNotify = new Set(Object.keys(updates).filter((key) => key !== 'userRole'));
  if (prevState.userRole !== state.userRole) directKeysToNotify.add('actualUserRole');
  if (prevState.simulatedUserRole !== state.simulatedUserRole) directKeysToNotify.add('simulatedUserRole');

  directKeysToNotify.forEach((key) => {
    if (listeners.has(key)) {
      listeners.get(key).forEach((callback) => {
        if (key === 'actualUserRole') {
          callback(state.userRole ?? null, prevState.userRole ?? null);
          return;
        }
        callback(state[key], prevState[key]);
      });
    }
  });

  if (prevPublicState.userRole !== nextPublicState.userRole && listeners.has('userRole')) {
    listeners.get('userRole').forEach((callback) => {
      callback(nextPublicState.userRole, prevPublicState.userRole);
    });
  }

  // Notifier les abonnés globaux
  if (listeners.has('*')) {
    listeners.get('*').forEach((callback) => {
      callback(nextPublicState, prevPublicState);
    });
  }
}

/**
 * S'abonner aux changements d'état
 * @param {string} key - Clé à surveiller ('*' pour tout)
 * @param {Function} callback - Fonction de callback
 * @returns {Function} - Fonction de désabonnement
 */
export function subscribe(key, callback) {
  if (!listeners.has(key)) {
    listeners.set(key, new Set());
  }
  listeners.get(key).add(callback);

  // Retourne la fonction de désabonnement
  return () => {
    listeners.get(key)?.delete(callback);
    if (listeners.get(key)?.size === 0) {
      listeners.delete(key);
    }
  };
}

/**
 * Publie un événement personnalisé
 * @param {string} eventName - Nom de l'événement
 * @param {any} detail - Données de l'événement
 */
export function publish(eventName, detail) {
  window.dispatchEvent(
    new CustomEvent(eventName, {
      detail,
      bubbles: true,
      composed: true,
    })
  );
}

/**
 * Réinitialise l'état
 */
export function resetState() {
  state = {
    user: null,
    isAuthenticated: false,
    currentEtablissement: null,
    currentSite: null,
    userRole: null,
    simulatedUserRole: null,
    mesEtablissements: [],
    uiStaffLabel: null,
    uiStaffLabelSiteId: null,
    currentPrestation: null,
    currentPrestations: null,
    currentEmploye: null,
    bookingDraft: null,
  };
  localStorage.removeItem('edena-state');
}

/**
 * Charge les établissements accessibles par l'utilisateur connecté
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function loadUserEtablissements() {
  // Import dynamique pour éviter les dépendances circulaires
  const { supabase } = await import('./supabaseClient.js');

  const { data, error } = await supabase.from('v_mes_etablissements').select(`
      etablissement_id
    `);

  if (error) {
    console.error('Erreur chargement établissements:', error);
    return { data: null, error };
  }

  if (data && data.length > 0) {
    // Charger les détails du premier établissement
    const { data: etablissement } = await supabase
      .from('etablissements')
      .select('*')
      .eq('id', data[0].etablissement_id)
      .maybeSingle();

    // Charger le premier site de cet établissement
    const { data: sites } = await supabase
      .from('sites')
      .select('*')
      .eq('etablissement_id', data[0].etablissement_id)
      .limit(1);

    setState({
      mesEtablissements: data,
      currentEtablissement: etablissement,
      currentSite: sites?.[0] || null,
    });
  } else if (state.userRole === 'super_admin') {
    const { data: etabsAll } = await supabase
      .from('etablissements')
      .select('*')
      .order('nom');
    if (etabsAll && etabsAll.length > 0) {
      const etablissement = etabsAll[0];
      const { data: sites } = await supabase
        .from('sites')
        .select('*')
        .eq('etablissement_id', etablissement.id)
        .limit(1);
      setState({
        mesEtablissements: etabsAll.map((e) => ({ etablissement_id: e.id })),
        currentEtablissement: etablissement,
        currentSite: sites?.[0] || null,
      });
    }
  }

  return { data, error };
}

/**
 * Charge un établissement par défaut (pour démo sans connexion)
 * @returns {Promise<void>}
 */
export async function loadDefaultEtablissement() {
  const { supabase } = await import('./supabaseClient.js');

  // Charger le premier établissement disponible
  const { data: etablissements } = await supabase
    .from('etablissements')
    .select('*')
    .limit(1);

  const etablissement = etablissements?.[0];

  if (!etablissement) {
    console.error('Aucun établissement trouvé dans la base');
    return;
  }

  // Charger le premier site
  const { data: sites } = await supabase
    .from('sites')
    .select('*')
    .eq('etablissement_id', etablissement.id)
    .limit(1);

  setState({
    currentEtablissement: etablissement,
    currentSite: sites?.[0] || null,
    userRole: null, // Pas de rôle si non connecté
    mesEtablissements: [],
  });

  }

/**
 * Charge un établissement spécifique depuis l'URL ou l'ID
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: object, error: object}>}
 */
export async function loadEtablissementById(etablissementId) {
  const { supabase } = await import('./supabaseClient.js');

  // Charger l'établissement
  const { data: etablissement, error: etabError } = await supabase
    .from('etablissements')
    .select('*')
    .eq('id', etablissementId)
    .maybeSingle();

  if (etabError || !etablissement) {
    console.error('Établissement non trouvé:', etablissementId);
    return { data: null, error: etabError };
  }

  // Charger le premier site de cet établissement
  const { data: sites } = await supabase
    .from('sites')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .limit(1);

  setState({
    currentEtablissement: etablissement,
    currentSite: sites?.[0] || null,
  });

    return { data: etablissement, error: null };
}

// Charger l'état au démarrage
loadState();
